
import React from "react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import type { MortgageData } from "../CalculatorTabs";
import { calculateMortgageData } from '@/utils/calculator/mortgageCalculator';
import type { AutoCalculateOptions } from "../utils/types";
import { formatCurrency } from "../utils/formatters";
import FundingFeeToggle from "./subcomponents/FundingFeeToggle";

interface USDALoanFieldsProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
}

const USDALoanFields: React.FC<USDALoanFieldsProps> = ({ mortgageData, setMortgageData, autoCalculate }) => {
  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <h3 className="font-medium text-sm">USDA Loan Details</h3>
      
      <div className="flex items-center space-x-2">
        <Checkbox 
          id="first-time-homebuyer-usda"
          checked={mortgageData.isFirstTimeHomebuyer}
          onCheckedChange={(checked) => {
            setMortgageData(prev => {
              const updatedData = {
                ...prev,
                isFirstTimeHomebuyer: checked as boolean
              };
              return calculateMortgageData(updatedData);
            });
          }} 
        />
        <Label 
          htmlFor="first-time-homebuyer-usda" 
          className="text-sm font-medium cursor-pointer"
        >
          First-Time Homebuyer
        </Label>
      </div>
      
      {/* USDA Funding Fee Toggle */}
      <FundingFeeToggle
        mortgageData={mortgageData}
        setMortgageData={setMortgageData}
        autoCalculate={autoCalculate}
        label="Roll Guarantee Fee into Loan"
        tooltipContent="When selected, the USDA upfront guarantee fee will be added to your loan amount instead of being paid at closing. This will increase your loan amount and monthly payment, but reduce your out-of-pocket costs at closing."
      />
      
      {/* USDA Funding Fee Information */}
      <div className="bg-muted rounded-md p-3 text-sm">
        <p className="font-medium">USDA Guarantee Fee:</p>
        <p>
          1.00% upfront guarantee fee ({formatCurrency(
            (mortgageData.homePrice - mortgageData.downPayment) * 0.01
          )})
          {mortgageData.rollFundingFeeIntoLoan && 
            <span className="text-emerald-600 ml-1">(Added to Loan)</span>
          }
        </p>
        <p className="mt-1">
          Annual fee: 0.35% of loan amount
        </p>
      </div>
    </div>
  );
};

export { USDALoanFields };
